import {
  DiscoveryData,
  DiscoveryDetails,
  DiscoveryRow,
  DiscoveryStatus,
  FeedbackReason,
} from "@radarly/models/discovery";
import {
  InsightPage,
  InsightPageRecurringSnapshot,
  InsightPageSnapshot,
  InsightPageSnapshotFactory,
  LeanInsightPage,
  RecurringSnapshotPayload,
} from "@radarly/models/insight-pages/model";

export interface Pagination {
  start: number;
  limit: number;
}

export interface InsightPageRepo {
  getInsightPages(project: number, workspaceId: number): Promise<InsightPage[]>;
  getInsightPage(project: number, id: InsightPage["id"]): Promise<InsightPage>;
  setInsightPage(
    project: number,
    page: InsightPage,
    template?: string
  ): Promise<InsightPage>;
  deleteInsightPage(
    project: number,
    id: InsightPage["id"]
  ): Promise<{ deleted: boolean }>;
  sendSnapshot(
    projectId: number,
    id: InsightPage["id"],
    snapshot: InsightPageSnapshotFactory
  ): Promise<InsightPageSnapshot>;
  sendRecurringSnapshot(
    projectId: number,
    id: InsightPage["id"],
    snapshot: RecurringSnapshotPayload
  ): Promise<InsightPageRecurringSnapshot>;
  shareInsightPageAccess(
    projectId: number,
    id: InsightPage["id"],
    access: InsightPage["access"],
    sharedWith: InsightPage["sharedWith"]
  ): Promise<InsightPage>;
  getRecurringSnapshots(
    projectId: number,
    id: InsightPage["id"],
    all?: boolean
  ): Promise<InsightPageRecurringSnapshot[]>;
  deleteRecurringSnapshot(
    projectId: number,
    pageId: InsightPage["id"],
    recurringSnapshotId: InsightPageRecurringSnapshot["id"]
  ): Promise<boolean>;
  getInvalidRecurringSnapshots(
    projectId: number,
    id: InsightPage["id"],
    timezone: InsightPage["timezone"]
  ): Promise<InsightPageRecurringSnapshot[]>;
  batchUpdateRecurringSnapshots(
    projectId: number,
    id: InsightPage["id"],
    snapshots: Array<{
      id: InsightPageRecurringSnapshot["id"];
      page: InsightPage;
    }>
  ): Promise<void>;
  getDiscoveryPage(
    project: number,
    workspaceId: number,
    params?: Record<string, unknown>
  ): Promise<DiscoveryData>;
  getDiscoveryStatus(
    project: number,
    workspaceId: number,
    params?: Record<string, unknown>
  ): Promise<DiscoveryStatus>;
  getDiscoveryDetails(
    project: number,
    workspaceId: number,
    meta: DiscoveryRow["meta"],
    template?: string
  ): Promise<DiscoveryDetails>;
  sendDiscoveryFeedback(
    projectId: number,
    workspaceId: number,
    page: InsightPage,
    feedback: boolean,
    reason: FeedbackReason,
    message?: string
  ): Promise<void>;
  searchPages(
    projectId: number,
    search: string,
    workspaceId: number,
    pagination: Pagination,
    sort: string
  ): Promise<InsightPage[]>;
  saveDiscoverySnapshot(
    projectId: number,
    snapshot: InsightPage
  ): Promise<{ s3Id: string }>;
  getLeanInsightPages(
    projectId: number,
    workspaceId: number,
    pagination: Pagination
  ): Promise<LeanInsightPage[]>;
}

export const InsightPageRepositoryUID = Symbol("Radarly/InsightPageRepository");
